/**
 * `dual_choice_hero` renderer — composes `@nextday/funnel-ui` `DualChoiceHero`.
 *
 * Presents exactly two options with rich imagery in one of two modes:
 *   • **split** — two image halves (tap a side to select)
 *   • **toggle** — single hero with pill toggle + Start CTA
 *
 * Stores the chosen option id under `data.answerKey`.
 *
 * Step JSON shape:
 *
 *   {
 *     "type": "dual_choice_hero",
 *     "data": {
 *       "mode": "split",
 *       "eyebrow": "CHOOSE YOUR VIBE",
 *       "headline": "Pick a side. Tap to start chatting.",
 *       "answerKey": "style_preference",
 *       "options": [
 *         { "id": "realistic", "label": "Realistic", "subLabel": "PHOTOREAL", "imageUrl": "..." },
 *         { "id": "anime", "label": "Anime", "subLabel": "STYLIZED", "imageUrl": "..." }
 *       ]
 *     }
 *   }
 */
import { DualChoiceHero } from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";
import { urlOrTemplate } from "@survey-kit/urlOrTemplate";

const dualChoiceOptionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  subLabel: z.string().optional(),
  imageUrl: urlOrTemplate,
});

export const dualChoiceHeroSchema = z
  .object({
    mode: z.enum(["split", "toggle"]).optional(),
    desktopOrientation: z.enum(["auto", "horizontal", "vertical"]).optional(),
    eyebrow: z.string().optional(),
    headline: z.string().optional(),
    subhead: z.string().optional(),
    options: z.array(dualChoiceOptionSchema).min(2).max(2),
    answerKey: z.string().min(1),
    ctaLabel: z.string().optional(),
    ctaPrefix: z.string().optional(),
    disclaimer: z.string().optional(),
    initialActiveId: z.string().optional(),
  })
  .strict();

export type DualChoiceHeroData = z.infer<typeof dualChoiceHeroSchema>;

const DualChoiceHeroRenderer = ({
  data,
  step,
  answers,
  onAnswer,
  onAdvance,
}: RendererProps<DualChoiceHeroData>) => {
  const previousAnswer =
    typeof answers[data.answerKey] === "string" ? (answers[data.answerKey] as string) : undefined;

  const handleSelect = (optionId: string): void => {
    onAnswer(data.answerKey, { value: optionId });
    onAdvance();
  };

  return (
    <DualChoiceHero
      mode={data.mode}
      desktopOrientation={data.desktopOrientation}
      eyebrow={data.eyebrow}
      headline={data.headline}
      subhead={data.subhead}
      options={data.options}
      ctaLabel={data.ctaLabel}
      ctaPrefix={data.ctaPrefix}
      disclaimer={data.disclaimer}
      initialActiveId={previousAnswer ?? data.initialActiveId}
      onSelect={handleSelect}
      className="-mx-5 -mt-5 -mb-6 flex-1"
      testId={`renderer-dual-choice-hero-${step.id}`}
    />
  );
};

export const dualChoiceHeroRenderer = createRenderer({
  type: "dual_choice_hero",
  dataSchema: dualChoiceHeroSchema,
  label: "Dual choice hero",
  wideLayout: true,
  Component: DualChoiceHeroRenderer,
});

export default dualChoiceHeroRenderer;
